import WorldActionButtons from '@/app/[locale]/[roomId]/components/WorldActionButtons'
import WorldLabels from '@/app/[locale]/[roomId]/components/WorldLabels'

import IconLike from '@/components/assets/icon_favorite_enable.svg'
import IconWatch from '@/components/assets/icon_watch.svg'
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { useUAContext } from '@/context/UAContext'
import { useWorldDetailsContext } from '@/context/WorldDetailsContext'
import { cn } from '@/lib/utils'
import { RefObject, useRef, useState } from 'react'
import { useResizeObserver } from 'usehooks-ts'

const Stats = () => {
  const { viewCountWithKilo, likeCountWithKilo } = useCheckEnterRoomContext()

  return (
    <div className="flex items-center gap-2 md:gap-4">
      <div className="flex items-center gap-1">
        <IconWatch className="size-4" />
        <span className="body2-omni-styles text-vive-primary-30">{viewCountWithKilo}</span>
      </div>

      <div className="flex items-center gap-1">
        <IconLike className="size-4" />
        <span className="body2-omni-styles text-vive-primary-30">{likeCountWithKilo}</span>
      </div>
    </div>
  )
}

function WorldInfoTitle() {
  const titleRef = useRef<HTMLHeadingElement>(null)
  const scaledTitleRef = useRef<HTMLHeadingElement>(null)
  const { is3DCommerce } = useWorldDetailsContext()
  const { roomData } = useCheckEnterRoomContext()
  const { isMobileLandscape, isMobile } = useUAContext()
  const maxLines = 3
  const lineHeight = isMobile ? 26 : 32
  const maxFontSize = isMobile ? 18 : 24
  const minFontSize = 8
  const scaleFactor = 0.95

  const [isOverflow, setIsOverflow] = useState(false)
  const [fontSize, setFontSize] = useState(maxFontSize)

  useResizeObserver({
    ref: titleRef as RefObject<HTMLElement>,
    onResize: () => {
      if (!titleRef.current) return

      const el = titleRef.current
      const lines = Math.round(el.scrollHeight / lineHeight)
      setIsOverflow(lines > maxLines)
    },
  })

  useResizeObserver({
    ref: scaledTitleRef as RefObject<HTMLElement>,
    onResize: () => {
      if (!scaledTitleRef.current) return

      const el = scaledTitleRef.current
      const maxHeight = lineHeight * maxLines
      let scale = 1

      el.style.fontSize = `${maxFontSize}px`
      el.style.lineHeight = `${lineHeight}px`

      while (el.scrollHeight > maxHeight && maxFontSize * scale > minFontSize) {
        scale *= scaleFactor
        el.style.fontSize = `${maxFontSize * scale}px`
      }
      setFontSize(maxFontSize * scale)
    },
  })

  return (
    <div className="relative">
      <div
        className={cn('flex justify-between gap-4', {
          'pointer-events-none invisible absolute': isOverflow,
        })}
      >
        <div className="flex flex-col">
          <h2
            ref={titleRef}
            className={cn(
              'flex items-center gap-2 overflow-hidden text-lg leading-6.5 font-extrabold tracking-[0.1px] break-all max-md:flex-1 md:text-2xl md:leading-8',
              {
                'md:text-lg md:leading-6.5': isMobileLandscape,
              },
            )}
          >
            {roomData.title}
            {is3DCommerce && <WorldLabels />}
          </h2>
          <Stats />
        </div>
        <WorldActionButtons />
      </div>
      <div className={cn({ hidden: !isOverflow })}>
        <h2
          ref={scaledTitleRef}
          className="flex items-center gap-2 font-extrabold tracking-[0.1px] break-all max-md:flex-1"
          style={{ fontSize: `${fontSize}px`, lineHeight: `${lineHeight}px` }}
        >
          {roomData.title}
          {is3DCommerce && <WorldLabels />}
        </h2>
        <div className={cn('mt-2 flex items-start justify-between')}>
          <Stats />
          <WorldActionButtons />
        </div>
      </div>
    </div>
  )
}

export default WorldInfoTitle
